/* eslint-disable react/jsx-no-bind */
/* eslint-disable jsx-a11y/label-has-for */
import React, { useState, useEffect, useRef, Fragment } from 'react';
import { object, array } from 'prop-types';
import globalVPS from 'js/global-vps';
import Chart from './chart';
import './css/index.css';
import { HoverContext } from './context';
import { getMaxYValue, getMinYValue } from './utils/getY';

const ChartContext = ( props ) => {

    const { chartColors, bulkData } = props;

    const [ width, setWidth ] = useState( props.componentWrapper ? props.componentWrapper.offsetWidth : 0 );
    const [ vps, setVps ] = useState( {
        isSVP: globalVPS.current( 'small' ),
        isMVP: globalVPS.current( 'medium' ),
        isLVP: globalVPS.current( 'large' ),
        currentVP: globalVPS.current().name
    } );

    const [ hover, setHover ] = useState( { xVal: undefined, yVal: undefined, d: undefined } );

    const pageWrapper = document.querySelector( '.page' );

    useEffect( () => {

        setVps( getViewports() );

        window.addEventListener( 'resize', handleResize );

        // Unmounting
        return () => {

            window.removeEventListener( 'resize', handleResize );

        };

    }, [] );

    useEffect( () => {

        if ( vps ) {

            bindViewports();

        }

    }, [ vps ] );

    /* Gets the width property based on window size */
    const handleResize = () => {

        // 10px padding on left + right
        const currWidth = props.componentWrapper.offsetWidth;

        if ( width === currWidth ) return;

        setWidth( currWidth );

    };

    const bindViewports = () => {

        return globalVPS( ( state ) => {

            if ( state.name === vps.currentVP ) return;
            setVps( getViewports() );

        } );

    };

    const getViewports = () => {

        return {
            isSVP: globalVPS.current( 'small' ),
            isMVP: globalVPS.current( 'medium' ),
            isLVP: globalVPS.current( 'large' ),
            currentVP: globalVPS.current().name
        };

    };

    /* Stores all the data together */
    const extractData = ( da, colors ) => {

        const rowLabels = da[0].slice( 2 );

        let result = [];

        if ( Array.isArray( da[1][0] ) ) {

            result = da.slice( 1 ).map( ( dataPoints, dIdx ) => {

                const rowObjs = [];

                const forDate = dataPoints[0][1].split( '-' );
                const currentDate = new Date( `${ forDate[0] }/${ forDate[1] }/${ forDate[2] }` );

                const colObj = {
                    date: currentDate,
                    index: dIdx
                };

                for ( const regionalPoints of dataPoints ) {

                    const forRegion = regionalPoints[0].split( '&' )[1];

                    const pointObjs = regionalPoints.slice( 2 ).map( ( r, rIdx ) => {

                        return {
                            name: `${ forRegion }_${ rowLabels[rIdx] }`,
                            value: r,
                            color: colors[forRegion][rowLabels[rIdx]]
                        };

                    } );

                    rowObjs.push( pointObjs );

                }

                return {
                    column: colObj,
                    rows: rowObjs.flat()
                };

            } );

        }
        else {

            const chartData = da.slice( 1 ).map( ( d ) => d.slice( 1 ) );

            result = chartData.map( ( cData, i ) => {

                const dateData = cData[0].split( '-' );
                const currentDate = new Date( `${ dateData[0] }/${ dateData[1] }/${ dateData[2] }` );

                const chartObj = {
                    column: {
                        date: currentDate,
                        value: cData.slice( 1 ),
                        index: i
                    },
                    rows: []
                };

                chartObj.rows = cData.slice( 1 ).map( ( d, j ) => {

                    const rowObj = {
                        name: rowLabels[j],
                        value: d,
                        color: chartColors[j]
                    };

                    return rowObj;

                } );

                return chartObj;

            } );

        }

        return result;

    };

    const generateKey = ( s, idx ) => {

        // make the key for charts contain data from each filter
        // such that if any of the three's state changes, we properly update
        // and re-render chart;
        return `${ JSON.stringify( s.region ) } ${ JSON.stringify( s.characteristic ) } ${ JSON.stringify( s.demographic ) } ${ idx }`;

    };

    const getYDomain = ( selectedData ) => {

        const yDomain = {
            minValue: 0,
            maxValue: 0
        };

        selectedData.forEach( ( data ) => {

            data.rows.forEach( ( v ) => {

                yDomain.minValue = parseFloat( v.value ) <= parseFloat( yDomain.minValue ) ? v.value : yDomain.minValue;
                yDomain.maxValue = parseFloat( v.value ) >= parseFloat( yDomain.maxValue ) ? v.value : yDomain.maxValue;

            } );

        } );

        return yDomain;

    };

    return (
        <Fragment>
            { bulkData.map( ( d, bIdx ) => {

                const chartWrapper = useRef();

                const [ format, values ] = d;
                const [ { data, state, description: { name, label, legend, colors } } ] = values;
                const { margin, height, yAxisTitle, disclaimer } = format;
                const chosenData = extractData( data, colors );

                const allSameUnits = bulkData.length > 1 && ( bulkData.every( ( d ) => d[0]['yAxisTitle'][0] === 'Percent' ) || bulkData.every( ( d ) => d[0]['yAxisTitle'][0] === 'Dollars' ) );

                const isPercentUnit = bulkData.every( ( d ) => d[0]['yAxisTitle'][0] === 'Percent' );
                const isDollarUnit = bulkData.every( ( d ) => d[0]['yAxisTitle'][0] === 'Dollars' );
                const isChangeInAll = bulkData.every( ( d ) => d[1][0]['description'].name.toLowerCase().includes( 'change in' ) );
                const isChangeInOne = bulkData.some( ( d ) => d[1][0]['description'].name.toLowerCase().includes( 'change in' ) );

                let yMin = 0;

                let yMax = 0;

                if ( isChangeInAll || isDollarUnit || ( isPercentUnit && !isChangeInOne ) ) {

                    for ( const [ , [ { data, description: { colors } } ] ] of bulkData ) {

                        const yDomain = getYDomain( extractData( data, colors ) );

                        if ( yDomain.minValue < yMin ) yMin = yDomain.minValue;

                        if ( yDomain.maxValue > yMax ) yMax = yDomain.maxValue;

                    }

                }
                else {

                    const yDomain = getYDomain( extractData( data, colors ) );

                    if ( yDomain.minValue < yMin ) yMin = yDomain.minValue;

                    if ( yDomain.maxValue > yMax ) yMax = yDomain.maxValue;

                }

                const isPercent = yAxisTitle[0] === 'Percent';

                yMin = getMinYValue( isPercent, yMin );
                yMax = getMaxYValue( isPercent, yMax, name.toLowerCase().includes( 'change in' ) );

                return (
                    <div key={ generateKey( state, bIdx ) } className={ `chart ${ bulkData.length > 1 ? 'multiple-chart' : '' }` } ref={ chartWrapper }>
                        <div className='chart-selection-section'>
                            <div className='chart-header-text-wrapper'>
                                <div className='chart-title-container'>
                                    <p className='svg-header'>{ name }</p>
                                </div>
                            </div>

                            <div className='svg-wrapper'>
                                <HoverContext.Provider value={{ hover, setHover }}>
                                    {
                                        <Chart
                                            data={ chosenData }
                                            rawData={ data }
                                            width={ width }
                                            label={ label }
                                            legend={ legend }
                                            title={ name }
                                            margin={ margin }
                                            height={ height }
                                            yAxisTitle={ yAxisTitle }
                                            multipleCharts={ bulkData.length > 1 }
                                            allSameUnits={ allSameUnits }
                                            colors={ chartColors }
                                            disclaimer={ disclaimer }
                                            componentWrapper={ chartWrapper.current }
                                            vps={ vps }
                                            startDate={ 0 }
                                            endDate={ chosenData.length || null }
                                            chartNo={ `chartNo-${ bIdx }` }
                                            yMin={ yMin }
                                            yMax={ yMax }
                                            pageWrapper={ pageWrapper }
                                        />
                                    }
                                </HoverContext.Provider>
                            </div>
                        </div>
                    </div>
                );

            } ) }
        </Fragment>
    );

};

ChartContext.propTypes = {
    componentWrapper: object,
    bulkData: array.isRequired,
    chartColors: array.isRequired
};


export default ChartContext;
